import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
  Building2,
  Plus,
  Search,
  Edit,
  Eye,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  Activity,
  RefreshCw,
  User,
  Download,
  X,
  ArrowLeft,
  LayoutGrid,
  List as ListIcon
} from 'lucide-react';
import api, { getFileUrl } from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import TableSkeleton from '../../components/Common/TableSkeleton';
import { sanitizeInput } from '../../utils/sanitize';
import useGlobalSearch from '../../hooks/useGlobalSearch';

/**
 * The College model's status enum accepts both casings and the DB holds a mix
 * ("Active" and "active"). Everything display-side normalises through this so
 * badges and the status dropdown agree with each other regardless of casing.
 */
const normalizeStatus = (status) => {
  if (!status) return 'Draft';
  const s = String(status).toLowerCase();
  if (s === 'pending approval' || s === 'pending') return 'Pending Approval';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const CollegeManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { searchQuery: searchTerm, setSearchQuery: setSearchTerm, debouncedSearchQuery: debouncedSearch, searchInputRef } = useGlobalSearch('', 400, () => setCurrentPage(1));
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    draft: 0,
    inactive: 0,
    totalStudents: 0
  });

  const [viewMode, setViewMode] = useState(() => localStorage.getItem('collegeViewMode') || 'list');
  const toggleViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('collegeViewMode', mode);
  };

  // Reset page to 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, typeFilter]);

  // Fetch whenever debounced search, filters, or page changes
  useEffect(() => {
    fetchColleges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, statusFilter, typeFilter, currentPage]);

  const fetchColleges = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(statusFilter && { status: statusFilter }),
        ...(typeFilter && { institutionType: typeFilter })
      };

      const { data } = await api.get('/colleges', { params });

      if (data.success) {
        setColleges(data.data);
        setTotalPages(data.totalPages);

        // Use stats from backend (global totals, not page-filtered)
        const activeColleges = data.stats?.activeColleges || 0;
        const backendInactive = data.stats?.inactiveColleges;
        const backendDraft = data.stats?.draftColleges;

        // Calculate missing values only if backend doesn't provide them
        // Avoid circular dependency by calculating independently
        let inactiveColleges = backendInactive !== undefined ? backendInactive : 0;
        let draftColleges = backendDraft !== undefined ? backendDraft : 0;

        setStats({
          total: data.total,
          active: activeColleges,
          draft: draftColleges,
          inactive: inactiveColleges,
          totalStudents: data.stats?.totalStudents || 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch colleges:', error);
      if (error.response?.status === 404) {
        toast.error('Colleges not found');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view colleges');
      } else if (error.response?.status === 500) {
        toast.error('Server error while loading colleges. Please try again later');
      } else if (!navigator.onLine) {
        toast.error('No internet connection. Please check your network');
      } else {
        toast.error('Failed to fetch colleges');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentPage, debouncedSearch, statusFilter, typeFilter]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchColleges();
    toast.success('Colleges refreshed!');
  };

  const handleConvertPermanent = async (collegeId) => {
    if (!window.confirm('Are you sure you want to convert this temporary institute to a permanent one? This will generate a new standard ID.')) return;
    try {
      const { data } = await api.put(`/colleges/${collegeId}/convert-permanent`);
      if (data.success) {
        toast.success('Institute converted to permanent successfully!');
        fetchColleges();
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Failed to convert institute. Please try again.');
    }
  };

  const handleStatusChange = async (collegeId, newStatus) => {
    if (!collegeId) {
      toast.error('Invalid institute selected');
      return;
    }

    try {
      const { data } = await api.put(`/colleges/${collegeId}`, { status: newStatus });
      if (data.success) {
        toast.success(`Institute status updated to ${newStatus}`);
        fetchColleges();
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.response?.data?.message || 'Failed to update status. Please try again.');
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setTypeFilter('');
    setCurrentPage(1);
  };

  const hasActiveFilters = searchTerm || statusFilter || typeFilter;

  // Only values that actually occur in the data — the first five are
  // institutionType, the last two governanceType (the API matches either).
  const typeOptions = [
    'College',
    'Central University',
    'Private University',
    'Deemed University',
    'Autonomous College',
    'Autonomous',
    'Private'
  ];

  // --- Export All Handler ---
  const handleExportAll = async () => {
    try {
      toast.loading('Fetching all colleges for export...');
      const params = {
        limit: 9999,
        page: 1,
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(statusFilter && { status: statusFilter }),
        ...(typeFilter && { institutionType: typeFilter })
      };

      const { data } = await api.get('/colleges', { params });

      if (data.success && data.data.length > 0) {
        const exportData = data.data.map(c => ({
          'College Name': sanitizeInput(c.collegeName),
          'Code': sanitizeInput(c.collegeCode),
          'Type': sanitizeInput(c.institutionType),
          'Status': sanitizeInput(c.status),
          'Students': c.totalStudents || 0,
          'Coordinator': sanitizeInput(c.coordinatorInfo?.name || 'N/A'),
          'Email': sanitizeInput(c.email),
          'Contact': sanitizeInput(c.contactNumber),
          'Joined': c.onboardingDate ? new Date(c.onboardingDate).toLocaleDateString() : 'N/A'
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Colleges');

        const dateStr = new Date().toISOString().split('T')[0];
        XLSX.writeFile(wb, `Colleges_All_Export_${dateStr}.xlsx`);
        toast.success(`Exported ${exportData.length} colleges!`);
      } else {
        toast.error('No colleges found to export');
      }
    } catch (error) {
      toast.error('Failed to export all colleges');
      console.error(error);
    }
  };

  const getStatusBadge = (status) => {
    const displayStatus = normalizeStatus(status);

    const badges = {
      'Active': { bg: 'bg-green-50 dark:bg-green-950/40 border border-green-100 dark:border-green-900/30', text: 'text-green-700 dark:text-green-400', icon: CheckCircle },
      'Draft': { bg: 'bg-slate-50 dark:bg-slate-900/40 border border-slate-200/60 dark:border-slate-800/30', text: 'text-slate-600 dark:text-slate-400', icon: Edit },
      'Inactive': { bg: 'bg-rose-50 dark:bg-rose-950/40 border border-rose-100 dark:border-rose-900/30', text: 'text-rose-700 dark:text-rose-400', icon: XCircle },
      'Suspended': { bg: 'bg-amber-50 dark:bg-amber-950/40 border border-amber-100 dark:border-amber-900/30', text: 'text-amber-700 dark:text-amber-400', icon: XCircle },
      'Pending Approval': { bg: 'bg-blue-50 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-900/30', text: 'text-blue-700 dark:text-blue-400', icon: Clock }
    };

    const badge = badges[displayStatus] || badges['Draft'];
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3 mr-1" />
        {displayStatus}
      </span>
    );
  };

  const StatCard = ({ icon: Icon, label, value, loading }) => (
    <div className="bg-white dark:bg-[#002147] rounded-xl p-4 border border-gray-100 dark:border-[#1a3884]/35 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden group">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide truncate mb-1">{label}</p>
          {loading ? (
            <div className="h-6 bg-gray-200 dark:bg-slate-700 rounded animate-pulse w-12 mt-1"></div>
          ) : (
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
          )}
        </div>
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00152E] to-[#1a3884] dark:from-[#002A5C] dark:to-[#1a3884] flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform duration-300">
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50/50 dark:bg-[#00152E]/5 p-4 lg:p-6 space-y-6 pb-24">
      {/* Header */}
      <div className="bg-white dark:bg-[#002147] rounded-2xl shadow-sm border border-gray-200 dark:border-[#1a3884]/35 p-4 mb-6 animate-fade-in">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2.5 bg-white dark:bg-[#002147] text-gray-600 dark:text-gray-300 hover:text-[#1a3884] dark:hover:text-blue-400 hover:bg-[#00152E]/5 dark:hover:bg-[#1a3884] dark:hover:border-[#1a3884] dark:hover:text-white rounded-xl transition-all border border-gray-200 dark:border-[#1a3884]/45 group shadow-sm flex items-center justify-center shrink-0"
              title="Back to Dashboard"
            >
              <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
            </button>
            <div className="p-2 bg-[#1a3884]/10 dark:bg-[#1a3884]/20 rounded-xl shrink-0">
              <Building2 className="w-6 h-6 text-[#1a3884] dark:text-[#4f7dff]" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-[#1a3884] dark:text-[#4f7dff] mb-1">
                Institute Management
              </h1>
              <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                <Activity size={14} className="text-[#1a3884] dark:text-[#4f7dff]" />
                <span className="font-medium">
                  Manage all institutions and their details
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap">
            <button
              onClick={handleExportAll}
              className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold px-3.5 py-2 text-gray-700 dark:text-gray-200 bg-white dark:bg-[#00152e] border border-gray-200 dark:border-[#1a3884]/50 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 hover:border-[#1a3884]/40 active:scale-95 rounded-xl shadow-sm transition-all duration-150 cursor-pointer select-none"
              title="Export All (matching filters)"
            >
              <Download size={15} className="text-gray-600 dark:text-gray-300 shrink-0" />
              <span className="hidden sm:inline">Export</span>
            </button>

            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold px-3.5 py-2 text-gray-700 dark:text-gray-200 bg-white dark:bg-[#00152e] border border-gray-200 dark:border-[#1a3884]/50 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 hover:border-[#1a3884]/40 active:scale-95 rounded-xl shadow-sm transition-all duration-150 cursor-pointer select-none disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
            >
              <RefreshCw className={`shrink-0 ${refreshing ? 'animate-spin' : ''}`} size={15} />
              <span className="hidden sm:inline">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
            </button>

            <div className="flex items-center gap-1 bg-gray-100 dark:bg-[#00152e] p-1 rounded-xl border border-gray-200 dark:border-[#1a3884]/50">
              <button
                onClick={() => toggleViewMode('grid')}
                className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-xs font-bold transition-all duration-200 active:scale-95 cursor-pointer select-none ${viewMode === 'grid' ? 'bg-white dark:bg-[#1a3884] text-[#1a3884] dark:text-white shadow-sm ring-1 ring-black/5 dark:ring-white/10' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
              >
                <LayoutGrid size={15} /> <span className="hidden lg:inline">Grid</span>
              </button>
              <button
                onClick={() => toggleViewMode('list')}
                className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-xs font-bold transition-all duration-200 active:scale-95 cursor-pointer select-none ${viewMode === 'list' ? 'bg-white dark:bg-[#1a3884] text-[#1a3884] dark:text-white shadow-sm ring-1 ring-black/5 dark:ring-white/10' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
              >
                <ListIcon size={15} /> <span className="hidden lg:inline">List</span>
              </button>
            </div>

            {user?.role === 'admin' && (
              <Link
                to="/colleges/onboard"
                className="inline-flex items-center gap-2 text-xs sm:text-sm px-4 py-2 bg-[#1a3884] hover:bg-[#0f255c] active:bg-[#0a1b42] active:scale-95 text-white hover:text-white active:text-white focus:text-white visited:text-white font-bold rounded-xl transition-all duration-150 shadow-sm hover:shadow-md border border-[#1a3884]/20 cursor-pointer select-none no-underline"
                style={{ color: '#ffffff' }}
              >
                <Plus size={16} className="text-white shrink-0" />
                <span className="text-white">Onboard Institute</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          icon={Building2}
          label="Total Institutes"
          value={stats.total}
          loading={loading}
        />
        <StatCard
          icon={CheckCircle}
          label="Active Institutes"
          value={stats.active}
          loading={loading}
        />
        <StatCard
          icon={XCircle}
          label="Inactive Institutes"
          value={stats.inactive}
          loading={loading}
        />
        <StatCard
          icon={Clock}
          label="Draft"
          value={stats.draft}
          loading={loading}
        />
      </div>

      {/* Filters & Search */}
      <div className="bg-white dark:bg-[#002147] rounded-2xl border border-gray-200 dark:border-[#1a3884]/35 shadow-sm p-4 mb-6">
        <div className="flex flex-col lg:flex-row flex-wrap items-start lg:items-center gap-3 w-full">
          
          {/* Search - Primary Control */}
          <div className="relative w-full lg:flex-1 min-w-[250px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by name or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 dark:border-[#1a3884]/45 rounded-xl text-sm focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff]/50 bg-gray-50 focus:bg-white dark:bg-[#00152E] dark:focus:bg-[#001f40] transition-all outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Filters Group */}
          <div className="flex flex-row items-center gap-3 w-full lg:w-auto">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="flex-1 sm:flex-none px-3 py-2 border border-gray-200 dark:border-[#1a3884]/45 rounded-xl text-sm focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff]/50 bg-gray-50 hover:bg-white focus:bg-white dark:bg-[#00152E] dark:hover:bg-[#001f40] dark:focus:bg-[#001f40] transition-all outline-none text-gray-900 dark:text-gray-100 min-w-[140px]"
            >
              <option value="">All Status</option>
              <option value="Active">Active</option>
              <option value="Draft">Draft</option>
              <option value="Inactive">Inactive</option>
            </select>

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="flex-1 sm:flex-none px-3 py-2 border border-gray-200 dark:border-[#1a3884]/45 rounded-xl text-sm focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff]/50 bg-gray-50 hover:bg-white focus:bg-white dark:bg-[#00152E] dark:hover:bg-[#001f40] dark:focus:bg-[#001f40] transition-all outline-none text-gray-900 dark:text-gray-100 min-w-[140px]"
            >
              <option value="">All Types</option>
              {typeOptions.map((type, idx) => (
                <option key={idx} value={type}>{type}</option>
              ))}
            </select>

            {hasActiveFilters && (
              <button
                onClick={handleClearFilters}
                className="px-3 py-2 text-sm text-gray-600 hover:text-red-600 font-medium transition-colors ml-1"
              >
                Reset
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content View (Grid or List) */}
      <div className="mb-6">
        {viewMode === 'list' ? (
          <div className="bg-white dark:bg-[#002147] rounded-2xl shadow-sm border border-gray-200 dark:border-[#1a3884]/30 overflow-hidden">
             <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-[#1a3884]/20 bg-gray-50/50 dark:bg-[#001f40]/20">
                <h2 className="font-semibold text-gray-800 dark:text-gray-100">Institutes Directory</h2>
                <span className="text-sm text-slate-400 dark:text-slate-200">
                    {stats.total} total &bull; Page {currentPage} of {totalPages}
                </span>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full">
                <thead className="bg-gray-50 dark:bg-[#001f40]/80 border-b border-gray-100 dark:border-[#1a3884]/20">
                <tr>
                    {['Institute Details', 'Coordinator', 'Type', 'Status', 'Stats', 'Joined', 'Actions'].map(h => (
                    <th key={h} className={`px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wide ${h === 'Actions' ? 'text-right' : 'text-left'}`}>
                        {h}
                    </th>
                    ))}
                </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-[#1a3884]/20">
                {loading ? (
                    <tr>
                    <td colSpan="7" className="px-4 py-8 text-center">
                        <TableSkeleton rows={5} columns={6} />
                    </td>
                    </tr>
                ) : colleges.length === 0 ? (
                    <tr>
                    <td colSpan="7" className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
                        <Building2 className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                        <p className="text-sm font-medium">No institutes found</p>
                        {hasActiveFilters && (
                        <button
                            onClick={handleClearFilters}
                            className="mt-2 text-xs text-[#1a3884] dark:text-[#4f7dff] hover:underline font-medium"
                        >
                            Clear filters to see all institutions
                        </button>
                        )}
                    </td>
                    </tr>
                ) : (
                    colleges.map((college) => {
                    return (
                        <tr key={college._id} className="border-b border-gray-100 dark:border-[#1a3884]/20 hover:bg-[#1a3884]/5 dark:hover:bg-[#001f40]/80 transition-all duration-200">
                        <td className="px-4 py-3">
                            <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0 w-11 h-11 bg-white rounded-xl border border-gray-200 dark:border-[#1a3884]/40 flex items-center justify-center shadow-sm overflow-hidden p-1">
                                {college.logo ? (
                                <img src={getFileUrl(college.logo)} className="w-full h-full object-contain" alt="Logo" />
                                ) : (
                                <Building2 className="w-5 h-5 text-[#1a3884]" />
                                )}
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                                    {sanitizeInput(college.collegeName)}
                                    {college.isTemporary && (
                                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                                            Temporary
                                        </span>
                                    )}
                                </p>
                                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">{sanitizeInput(college.collegeCode)}</p>
                            </div>
                            </div>
                        </td>

                        {/* Coordinator */}
                        <td className="px-4 py-3">
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{sanitizeInput(college.coordinatorInfo?.name || 'Not Assigned')}</p>
                            <p className="text-xs text-slate-400 dark:text-slate-300 font-medium">{sanitizeInput(college.coordinatorInfo?.email || '-')}</p>
                        </td>

                        {/* Type */}
                        <td className="px-4 py-3 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap bg-[#00152E]/5 dark:bg-[#1a3884]/20 text-[#1a3884] dark:text-blue-300 border border-[#1a3884]/20">
                            {sanitizeInput(college.institutionType)}
                            </span>
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3 whitespace-nowrap">
                            {getStatusBadge(college.status)}
                        </td>

                        {/* Stats */}
                        <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center text-sm">
                            <Users className="w-4 h-4 mr-1.5 text-[#1a3884] dark:text-blue-400" />
                            <span className="font-semibold text-gray-900 dark:text-gray-100">{college.totalStudents || 0}</span>
                            <span className="text-slate-400 dark:text-slate-300 ml-1">Students</span>
                            </div>
                        </td>

                        {/* Joined Date */}
                        <td className="px-4 py-3 whitespace-nowrap">
                            {(() => {
                                const onboardDate = new Date(college.onboardingDate);
                                return college.onboardingDate && !isNaN(onboardDate) ? (
                                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                        {onboardDate.toLocaleDateString('en-GB')}
                                    </p>
                                ) : (
                                    <span className="text-sm text-gray-400">—</span>
                                );
                            })()}
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3 text-right text-sm font-medium whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1.5">
                            <button
                                onClick={() => navigate(`/colleges/${college._id}`)}
                                className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 active:scale-90 rounded-xl transition-all border border-transparent hover:border-blue-200 dark:hover:border-blue-800/40 cursor-pointer"
                                title="View Details"
                            >
                                <Eye className="w-4 h-4" />
                            </button>
                            {user?.role === 'admin' && (
                                <>
                                <button
                                    onClick={() => navigate(`/colleges/edit/${college._id}`)}
                                    className="p-2 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 active:scale-90 rounded-xl transition-all border border-transparent hover:border-emerald-200 dark:hover:border-emerald-800/40 cursor-pointer"
                                    title="Edit"
                                >
                                    <Edit className="w-4 h-4" />
                                </button>
                                <select
                                    value={normalizeStatus(college.status)}
                                    onChange={(e) => handleStatusChange(college._id, e.target.value)}
                                    className="px-2.5 py-1.5 text-xs font-semibold border border-gray-200 dark:border-[#1a3884]/40 bg-white dark:bg-[#002147] text-gray-700 dark:text-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a3884] outline-none cursor-pointer"
                                    title="Change Status"
                                >
                                    <option value="Active">Active</option>
                                    <option value="Draft">Draft</option>
                                    <option value="Inactive">Inactive</option>
                                </select>
                                {college.isTemporary && (
                                    <button
                                        onClick={() => handleConvertPermanent(college._id)}
                                        className="px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider bg-[#00152E] hover:bg-[#002A5C] active:scale-95 text-white rounded-xl transition-all ml-1 shadow-sm cursor-pointer"
                                        title="Convert to Permanent Institute"
                                    >
                                        Turn Permanent
                                    </button>
                                )}
                                </>
                            )}
                            </div>
                        </td>
                        </tr>
                    );
                    })
                )}
                </tbody>
            </table>
            </div>

          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                   <div key={i} className="h-64 bg-gray-100 dark:bg-[#00152e] rounded-2xl animate-pulse"></div>
                ))
            ) : colleges.length === 0 ? (
                 <div className="col-span-full text-center py-12 bg-white dark:bg-[#002147] rounded-2xl border border-gray-200 dark:border-[#1a3884]/30">
                    <Building2 className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">No institutes found</h3>
                    <p className="text-gray-500 dark:text-gray-400 mt-1">Try adjusting your filters or search term.</p>
                </div>
            ) : (
              colleges.map((college) => (
                <div key={college._id} className="group bg-white dark:bg-[#002147] rounded-2xl border border-gray-200 dark:border-[#1a3884]/30 p-5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 relative overflow-hidden flex flex-col">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#00152E] to-[#1a3884] dark:from-[#4f7dff] dark:to-[#1a3884] opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    
                    <div className="flex justify-between items-start mb-4">
                        <div className="w-12 h-12 rounded-xl bg-white border border-gray-200 dark:border-[#1a3884]/40 flex items-center justify-center shadow-sm shrink-0 overflow-hidden p-1.5">
                            {college.logo ? <img src={getFileUrl(college.logo)} alt={college.collegeName} className="w-full h-full object-contain" /> : <Building2 className="w-6 h-6 text-[#1a3884]" />}
                        </div>
                        {getStatusBadge(college.status)}
                    </div>

                    <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight mb-1 line-clamp-2">{sanitizeInput(college.collegeName)}</h3>
                    <p className="text-xs font-semibold text-gray-500 dark:text-[#4f7dff] mb-4 uppercase tracking-wider">{sanitizeInput(college.collegeCode)} • {sanitizeInput(college.institutionType)}</p>
                    
                    <div className="space-y-2 mt-auto pt-4 border-t border-gray-100 dark:border-[#1a3884]/30">
                        <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center text-gray-600 dark:text-gray-300">
                                <Users className="w-4 h-4 mr-2 text-gray-400" />
                                <span>Students</span>
                            </div>
                            <span className="font-bold text-gray-900 dark:text-white">{college.totalStudents || 0}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center text-gray-600 dark:text-gray-300">
                                <User className="w-4 h-4 mr-2 text-gray-400" />
                                <span>Coordinator</span>
                            </div>
                            <span className="font-semibold text-gray-900 dark:text-white truncate max-w-[120px]">{sanitizeInput(college.coordinatorInfo?.name || '-')}</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 mt-5">
                    <button
                        onClick={() => navigate(`/colleges/${college._id}`)}
                        className="w-full py-2 bg-gray-50 dark:bg-[#1a3884]/20 hover:bg-gray-100 dark:hover:bg-[#1a3884]/40 active:scale-95 text-gray-700 dark:text-gray-200 rounded-xl text-xs sm:text-sm font-semibold flex items-center justify-center gap-1.5 transition-all border border-gray-200 dark:border-[#1a3884]/30 cursor-pointer"
                    >
                        <Eye size={14} /> View
                    </button>
                    {user?.role === 'admin' && (
                        <button
                        onClick={() => navigate(`/colleges/edit/${college._id}`)}
                        className="w-full py-2 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 active:scale-95 text-blue-600 dark:text-blue-400 rounded-xl text-xs sm:text-sm font-semibold flex items-center justify-center gap-1.5 transition-all border border-blue-100 dark:border-blue-900/30 cursor-pointer"
                        >
                        <Edit size={14} /> Edit
                        </button>
                    )}
                    </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Pagination — shared by BOTH views. It used to live inside the list
            branch only, which left grid users stranded on page 1 with no way
            to reach the remaining institutes. */}
        {!loading && totalPages > 1 && (
          <div className="mt-4 px-6 py-4 rounded-2xl border border-gray-200 dark:border-[#1a3884]/30 bg-white dark:bg-[#002147] flex items-center justify-between shadow-sm">
            <div className="text-xs font-medium text-slate-600 dark:text-slate-300">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 dark:border-[#1a3884]/40 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#002d5c] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 dark:border-[#1a3884]/40 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#002d5c] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default CollegeManagement;
